import React, { useMemo, useState } from "react";
import { Filter } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { ProductCard } from "@/components/ProductCard";
import { SectionHeading } from "@/components/SectionHeading";
import { products } from "@/data/products";

const categories = ["All", "Vegetables", "Fruits", "Seasonal"];
const availability = ["All", "In Stock", "Out of Season"];

export default function Shop() {
  const [category, setCategory] = useState("All");
  const [stock, setStock] = useState("All");
  const [sort, setSort] = useState("Popular");
  const reduceMotion = useReducedMotion();

  const filtered = useMemo(() => {
    const result = products.filter((product) => {
      const categoryMatch = category === "All" || product.category === category || (category === "Seasonal" && product.availability !== "In Stock");
      const stockMatch = stock === "All" || product.availability === stock;
      return categoryMatch && stockMatch;
    });
    if (sort === "Price") return [...result].sort((a, b) => a.price - b.price);
    if (sort === "New Harvest") return [...result].reverse();
    return result;
  }, [category, stock, sort]);

  return (
    <main className="bg-[#FAFAF8] pt-32" data-testid="shop-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <SectionHeading eyebrow="Shop" title="Fresh hill harvests, edited like a premium collection." copy="Filter by produce type, availability and harvest preference. Prices and pack sizes are placeholders for quick future editing." testId="shop-heading" />
        <motion.div initial={reduceMotion ? false : { opacity: 0, y: 18 }} animate={reduceMotion ? {} : { opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="mb-10 rounded-[2rem] border border-[#EAEAE6] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="shop-filters-panel">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-[#2A4B3C]"><Filter className="h-4 w-4" /> Filters</div>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-sm font-medium text-[#4A3B32]">Category
              <select data-testid="shop-category-filter" value={category} onChange={(event) => setCategory(event.target.value)} className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-4 outline-none">
                {categories.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-[#4A3B32]">Availability
              <select data-testid="shop-availability-filter" value={stock} onChange={(event) => setStock(event.target.value)} className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-4 outline-none">
                {availability.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium text-[#4A3B32]">Sort
              <select data-testid="shop-sort-filter" value={sort} onChange={(event) => setSort(event.target.value)} className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-4 outline-none">
                {['Popular', 'Price', 'New Harvest'].map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
          </div>
        </motion.div>
        <p data-testid="shop-results-count" className="mb-6 text-sm text-[#6B6B66]">Showing {filtered.length} harvests</p>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-product-grid">
          {filtered.map((product, index) => <ProductCard key={product.id} product={product} index={index} />)}
        </div>
      </section>
    </main>
  );
}